<template>
  <div class="hero-carousel swiper-container" ref="slider">
    <div class="swiper-wrapper">
      <div
        v-for="(slide, index) in slides"
        :key="index"
        class="swiper-slide slide"
      >
        <div
          class="overlay"
          :style="{
            backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})`,
          }"
        />
        <div
          v-if="!slide.video_url"
          class="slide-background"
          :class="{ 'has-mobile': slide.mobile_image }"
        >
          <div
            class="desktop"
            :style="
              slide.background_image ? slideStyle(slide.background_image) : {}
            "
          ></div>
          <div
            class="mobile"
            v-if="slide.mobile_image"
            :style="slideStyle(slide.mobile_image)"
          ></div>
        </div>

        <div v-if="slide.video_url" class="container slide-video">
          <video class="video" muted autoplay loop playsinline>
            <source :src="slide.video_url" type="video/mp4" />
          </video>
        </div>
      </div>
    </div>

    <div class="slide-info">
      <div class="slide-info__container">
        <smooth-reflow>
          <h2 class="headline">{{ currentSlide.headline }}</h2>
          <p class="lead desktop" v-html="currentSlide.lead"></p>
          <p class="lead mobile" v-html="currentSlide.lead_mobile"></p>
          <p>
            <a
              :href="currentSlide.cta_url"
              class="button button--light"
              v-if="currentSlide.cta_label"
              >{{ currentSlide.cta_label }}</a
            >
          </p>
        </smooth-reflow>
      </div>
    </div>

    <div class="slider-progress">
      <div class="slider-progress__container">
        <div class="meta">
          <div class="meter">
            <div class="progress" ref="progress"></div>
          </div>
          <div class="page">
            {{ currentSlideIndex + 1 }}/{{ slides.length }}
          </div>
          <div class="navigation">
            <div class="navigation__button navigation__button--prev">
              <i class="fal fa-chevron-left"></i>
            </div>
            <div class="navigation__button navigation__button--next">
              <i class="fal fa-chevron-right"></i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="scroll">
      <winners-scroll @click="handleWinnersScroll" />
    </div>
  </div>
</template>

<script>
import Swiper from "swiper/dist/js/swiper.js";;
import SmoothReflow from "./smooth-reflow.vue";
import WinnersScroll from "./winners-scroll.vue";

export default {
  name: "HeroCarousel",
  selector: "#vue-hero-carousel",
  components: { SmoothReflow, WinnersScroll },
  data() {
    return {
      currentSlideIndex: 0,
      timestamp: Date.now(),
    };
  },
  props: {
    slides: {
      type: Array,
      required: true,
    },
    delay: {
      type: Number,
      default: 7,
    },
    overlayOpacity: {
      type: Number,
      default: 50,
    },
  },
  methods: {
    slideStyle(backgroundUrl) {
      return {
        backgroundImage: `
          url(${backgroundUrl})
        `,
      };
    },
    handleWinnersScroll() {
      const bcr = this.$el.getBoundingClientRect();
      window.scrollToPosition(bcr.height);
    },
  },
  computed: {
    currentSlide() {
      return this.slides[this.currentSlideIndex];
    },
    isCurrentSlideVideo() {
      return Boolean(this.currentSlide.video_url);
    },
    actualDelay() {
      return this.delay * 1000;
    },
  },
  mounted() {
    this.swiper = new Swiper(this.$refs.slider, {
      effect: "fade",
      autoplay: {
        delay: this.actualDelay,
        disableOnInteraction: false,
      },
      navigation: {
        prevEl: ".navigation__button--prev",
        nextEl: ".navigation__button--next",
        disabledClass: "navigation__button--disabled",
      },
    });
    this.swiper.on("slideChange", () => {
      this.timestamp = Date.now();
      this.currentSlideIndex = this.swiper.activeIndex;
    });

    this.swiper.on("slideChangeTransitionStart", () => {
      // Swiper will call slideChange before slideChangeTransitionStart, so index has been updated
      const nextSlideIndex = this.currentSlideIndex;
      const slideEl = this.$el.querySelectorAll(".swiper-slide")[
        this.currentSlideIndex
      ];
      const video = slideEl.querySelector("video");
      if (video) video.currentTime = 0;
    });

    const loop = () => {
      const msElapsed = Date.now() - this.timestamp;
      const percentage = msElapsed / this.actualDelay;
      this.$refs.progress.style.transform = `scaleX(${percentage})`;
      window.requestAnimationFrame(loop);
    };

    loop();
  },
};
</script>

<style lang="scss" scoped>
.hero-carousel {
  /* height: 100vh; */
  height: 798px;
  width: 100%;
  position: relative;

  @include media("<=desktop") {
    height: 100vh;
  }

  @include media("height<798px") {
    height: 100vh;
  }

  @include media("<=tablet") {
    height: 50vh;
  }

  @include media("<=621px") {
    height: 480px;
  }
}

.slide-background {
  width: 100%;
  height: 100%;

  & > * {
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
  }

  .desktop {
    display: block;
  }

  .mobile {
    display: none;
  }

  @include media("<=l-phone") {
    &.has-mobile {
      .desktop {
        display: none;
      }

      .mobile {
        display: block;
      }
    }
  }
}

.container {
  @include inner-container();
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.headline {
  font-weight: 700;
  font-size: 64px;
  color: $white;
  letter-spacing: 0;
  line-height: 70px;
  margin: none;
  width: 50%;

  @include media("<=desktop") {
    font-size: 48px;
    line-height: 48px;
  }

  @include media("<=tablet") {
    width: 100%;
  }

  @include media("<=l-phone") {
    font-size: 28px;
    line-height: 32px;
  }
}

.lead {
  margin: 32px 0 0 0;
  font-size: 21px;
  color: $white;
  letter-spacing: 0;
  line-height: 28px;
  width: 66.7%;

  &.desktop {
    display: block;
  }

  &.mobile {
    display: none;
  }

  @include media("<=desktop") {
    font-size: 18px;
    line-height: 24px;
  }

  @include media("<=tablet") {
    width: 100%;
    &.desktop {
      display: none;
    }
    &.mobile {
      display: block;
    }
  }
}

.button {
  margin-top: 40px;
}

.meta {
  margin-top: 48px;
  display: flex;
  align-items: center;
  color: $white;
  font-size: 10px;
  letter-spacing: 0;
}

.meter {
  width: 130px;
  height: 2px;
  background: rgba($white, 0.2);
  overflow: hidden;
}

.progress {
  background: $teal-light;
  height: 2px;
  transform-origin: left;
}

.page {
  margin-left: 16px;
  font-weight: 700;
}

.navigation {
  display: flex;
  margin-left: 48px;
}

.navigation__button {
  cursor: pointer;
  &--next {
    margin-left: 16px;
  }

  &--disabled {
    opacity: 0.2;
  }
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.video {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: -1;
  min-width: 100%;
  min-height: 100%;
  @include media("<=tablet") {
    max-height: 50vh;
  }
}

.slide-info {
  position: absolute;
  width: 100%;
  top: 50%;
  left: 0;
  z-index: 2;
  transform: translateY(-50%);

  &__container {
    @include inner-container();

    @include media("<=tablet") {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      padding-left: 24px;
      padding-right: 24px;
    }

    @include media("<=l-phone") {
      padding-left: 16px;
      padding-right: 16px;
    }
  }
}

.slider-progress {
  position: absolute;
  width: 100%;
  left: 0;
  bottom: 15%;
  z-index: 2;

  &__container {
    @include inner-container();

    @include media("<=tablet") {
      padding-left: 24px;
      padding-right: 24px;
    }

    @include media("<=l-phone") {
      padding-left: 16px;
      padding-right: 16px;
      display: flex;
      justify-content: center;
    }
  }

  @include media("<=l-phone") {
    bottom: 40px;
  }
}

.scroll {
  position: absolute;
  bottom: 40px;
  left: 40px;
  z-index: 1;
}

.square {
  position: absolute;
  z-index: 1;
  bottom: 0;
  right: 0;
  width: 720px;
  transform: translate(45%, 37%) rotate(19deg);

  ::v-deep svg {
    path {
      fill: $teal-light;
    }
  }

  @include media("<=tablet") {
    display: none;
  }
}
</style>
